// Bausteine der Rechnungs-Detailseite (design_handoff_rechnungen §5, Screens 08–10, 16): Widget-Hülle mit
// farbigem Symbol, Info-Zeilen mit Kopierknopf, Aktionszeilen, Chips, Werkzeugknöpfe und Verlaufssymbole.
// Reine Darstellung — Logik und Daten bleiben in components/RechnungDetailsSidebar.tsx.
import type { CSSProperties, ReactNode } from 'react';
import {
  ArrowCounterClockwise, CaretRight, ChatCircle, CheckCircle, ClipboardText, Coins, Copy, Eye, Link, PaperPlaneTilt, ShieldCheck, User, Wrench, type Icon,
} from '@phosphor-icons/react';
import type { TimelineEventType } from '../../../../lib/rechnungen/types';

/** Verlaufssymbole statt Emojis (Handoff §5); Farben bleiben aus TIMELINE_CONFIG. */
export const VERLAUF_ICON: Record<TimelineEventType, Icon> = {
  kommentar: ChatCircle,
  zuweisung: User,
  tech_pruefung_angefordert: Wrench,
  tech_pruefung_ergebnis: CheckCircle,
  freigabe_angefordert: Eye,
  freigabe_ergebnis: ShieldCheck,
  status_aenderung: ClipboardText,
  bezahlung: Coins,
  et_weitergeleitet: PaperPlaneTilt,
  korrektur_angefordert: ArrowCounterClockwise,
  korrektur_zurueckgezogen: ArrowCounterClockwise,
  korrektur_verknuepft: Link,
};

export function Widget({ titel, icon: I, farbe, rechts, children, gap = 8, style, schlicht = false }: {
  titel?: ReactNode; icon?: Icon; farbe?: string; rechts?: ReactNode; children: ReactNode; gap?: number; style?: CSSProperties;
  /** Symbol ohne farbiges Quadrat (Bearbeiten-Widgets, Screen 13) */
  schlicht?: boolean;
}) {
  return (
    <section className="immo-glas flex min-w-0 flex-col" style={{ borderRadius: 24, padding: 16, gap, ...style }}>
      {titel && (
        <div className="flex items-center" style={{ gap: 8, fontSize: 15, fontWeight: 800 }}>
          {I && schlicht && <I aria-hidden size={17} weight="fill" color="var(--acc)" className="shrink-0" />}
          {I && !schlicht && (
            <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 30, height: 30, borderRadius: 10, background: farbe ?? 'var(--acc)', color: '#fff' }}>
              <I size={15} weight="fill" />
            </span>
          )}
          <h2 className="m-0 min-w-0 flex-1 truncate" style={{ fontSize: 15, fontWeight: 800 }}>{titel}</h2>
          {rechts}
        </div>
      )}
      {children}
    </section>
  );
}

/** Auf Touch-Geräten 44-px-Tippfläche für die Kopierknöpfe (Entscheidung 17.10.2026). */
const istTouch = (): boolean => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;

export function KopierKnopf({ onClick, tip = 'Kopieren', groesse: g = 26, hell = false }: { onClick: () => void; tip?: string; groesse?: number; hell?: boolean }) {
  const groesse = istTouch() ? Math.max(44, g) : g;
  return (
    <button type="button" onClick={onClick} aria-label={tip} data-tip={tip} className="inline-flex shrink-0 items-center justify-center"
      style={{ width: groesse, height: groesse, borderRadius: Math.round(groesse / 3), border: 'none', cursor: 'pointer',
        background: hell ? 'rgba(255,255,255,0.2)' : 'var(--fill2)', color: hell ? 'inherit' : 'var(--ink2)' }}>
      <Copy size={Math.round(groesse / 2.2)} weight="bold" />
    </button>
  );
}

/** Zeile „Label · Wert“ mit Trennlinie; optional Symbol links und Kopierknopf rechts. */
export function InfoZeile({ label, wert, onKopieren, icon: I, letzte = false }: {
  label: string; wert: ReactNode; onKopieren?: () => void; icon?: Icon; letzte?: boolean;
}) {
  return (
    <div className="flex items-center justify-between" style={{ gap: 12, padding: '10px 0', borderBottom: letzte ? 'none' : '1px solid var(--line)' }}>
      <span className="flex shrink-0 items-center whitespace-nowrap" style={{ gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--ink3)' }}>
        {I && <I size={14} />}{label}
      </span>
      <span className="flex min-w-0 items-center" style={{ gap: 6 }}>
        <span className="text-right" style={{ fontSize: 13.5, fontWeight: 700, overflowWrap: 'anywhere' }}>{wert}</span>
        {onKopieren && <KopierKnopf onClick={onKopieren} />}
      </span>
    </div>
  );
}

/** Kleine Kachel mit Betrag (Netto/MwSt./Brutto) und Kopierknopf. */
export function BetragKachel({ label, wert, onKopieren }: { label: string; wert: string; onKopieren: () => void }) {
  return (
    <div className="flex items-center justify-between" style={{ gap: 6, padding: '10px 12px', borderRadius: 14, background: 'var(--fill)' }}>
      <span className="flex min-w-0 flex-col">
        <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink3)' }}>{label}</span>
        <span className="tabular-nums" style={{ fontSize: 14, fontWeight: 800 }}>{wert}</span>
      </span>
      <KopierKnopf onClick={onKopieren} />
    </div>
  );
}

/** Aktionszeile im Widget (Sachl. Prüfung, Freigabe anfordern, An Eigentümer senden …): Symbol, Text, Pfeil. */
export function AktionsZeile({ icon: I, farbe, label, onClick, tip, gesperrt = false, pfeil = true, gross = false, laeuft = false }: {
  icon: Icon; farbe: string; label: ReactNode; onClick: () => void; tip?: string; gesperrt?: boolean; pfeil?: boolean; gross?: boolean; laeuft?: boolean;
}) {
  return (
    <button type="button" onClick={onClick} disabled={gesperrt} data-tip={tip}
      className={`${gross ? 'immo-glas ' : ''}immo-zeile-hover flex w-full items-center text-left`}
      style={{ minHeight: gross ? 50 : 44, padding: gross ? '0 16px' : '0 14px', gap: 10, borderRadius: gross ? 20 : 14, border: 'none', cursor: gesperrt ? 'default' : 'pointer',
        fontFamily: 'inherit', fontSize: gross ? 14 : 13.5, fontWeight: 700, color: 'var(--ink)', background: gross ? undefined : 'var(--fill)', opacity: gesperrt ? 0.5 : 1 }}>
      <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: gross ? 30 : 28, height: gross ? 30 : 28, borderRadius: gross ? 10 : 9, background: farbe, color: '#fff' }}>
        <I size={gross ? 15 : 14} weight={laeuft ? 'bold' : 'fill'} className={laeuft ? 'animate-spin' : ''} />
      </span>
      <span className="min-w-0 flex-1">{label}</span>
      {pfeil && <CaretRight size={12} weight="bold" color="var(--ink3)" className="shrink-0" />}
    </button>
  );
}

export function DetailChip({ label, icon: I, farbe, flaeche }: { label: string; icon: Icon; farbe: string; flaeche: string }) {
  return (
    <span className="inline-flex max-w-full items-center" style={{ height: 26, padding: '0 10px', gap: 5, borderRadius: 13, fontSize: 11.5, fontWeight: 800, background: flaeche, color: farbe }}>
      <I size={12} weight="fill" className="shrink-0" /><span className="truncate">{label}</span>
    </span>
  );
}

/** Knopf der Werkzeugleiste im Kopf (Dokument, PDF, QR-Code, Archivieren, Löschen …). */
export function WerkzeugKnopf({ icon: I, label, onClick, tip, gefahr = false, akzent = false, gesperrt = false, laeuft = false, mobil = false }: {
  icon: Icon; label?: string; onClick: () => void; tip?: string; gefahr?: boolean; akzent?: boolean; gesperrt?: boolean; laeuft?: boolean; mobil?: boolean;
}) {
  const h = mobil ? 44 : 36;
  return (
    <button type="button" onClick={onClick} disabled={gesperrt || laeuft} data-tip={tip} aria-label={label ? undefined : tip}
      className={akzent ? 'inline-flex items-center justify-center' : 'immo-zeile-hover inline-flex items-center justify-center'}
      style={{ height: h, minWidth: h, padding: label ? '0 12px' : 0, gap: 6, borderRadius: 16, border: 'none', cursor: gesperrt ? 'default' : 'pointer', fontFamily: 'inherit',
        fontSize: 12.5, fontWeight: akzent ? 800 : 700, opacity: gesperrt ? 0.5 : 1,
        background: akzent ? 'var(--acc-fill)' : 'transparent', boxShadow: akzent ? 'var(--acc-sh)' : 'none',
        color: akzent ? 'var(--acc-ink)' : gefahr ? 'var(--bad)' : 'var(--ink)' }}>
      <I size={label ? 14 : 15} weight="bold" className={laeuft ? 'animate-spin' : ''} />
      {label}
    </button>
  );
}
